import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax.js';
import { TrackingType } from '../training-sessions/index.js';
import { ExercisePerformanceType } from './enums/exercise-performance-type.enum.js';
dayjs.extend(minMax);
export const ACTIVITY_POINT_PER_SECOND = 0.1;
export const str_pad_left = (number, pad, length) => {
    return (Array.from({ length: length + 1 }).join(pad) + number).slice(-length);
};
// takes a weight in grams and returns a string representation in kg with one decimal place
export const displayWeight = (weight) => {
    return (Math.round((weight || 0) / 100) / 10).toString().replace('.', ',');
};
export const getNetExerciseProgramItemDuration = (item) => {
    if (!item.exercise || !item.exercise.duration) {
        return 0;
    }
    const singleSetDuration = item.performanceType === ExercisePerformanceType.REPITITION
        ? item.exercise.duration * item.performanceContent
        : item.performanceContent;
    return singleSetDuration * item.setCount;
};
export const getNetExerciseProgramSeconds = (exerciseProgramItems) => {
    return exerciseProgramItems
        .filter((item) => item.exercise && item.exercise.duration)
        .flatMap((item) => getNetExerciseProgramItemDuration(item))
        .reduce((a, b) => a + b, 0);
};
export const getNetExerciseProgramActivityPoints = (exerciseProgramItems) => {
    return Math.round(getNetExerciseProgramSeconds(exerciseProgramItems) * ACTIVITY_POINT_PER_SECOND);
};
export const getTrainingSessionActivityPoints = (trainingSessionItems) => {
    const completedTrainingSessionItems = trainingSessionItems.filter((item) => item.trainingSessionItemTrackings.find((tracking) => tracking.type === TrackingType.COMPLETE));
    return getNetExerciseProgramActivityPoints(completedTrainingSessionItems.map((item) => item.exerciseProgramItem));
};
export const getTotalExerciseProgramSeconds = (exerciseProgramItems) => {
    if (exerciseProgramItems.length === 0) {
        return 0;
    }
    const netExerciseTime = getNetExerciseProgramSeconds(exerciseProgramItems);
    if (!netExerciseTime) {
        return 0;
    }
    const totalSetPauseTime = exerciseProgramItems
        .filter((item) => item.setCount > 1)
        .reduce((acc, item) => acc + (item.setCount - 1) * item.pauseDuration, 0); // count only pauses, thus setCount-1
    const EXERCISE_PAUSE_DURATION = 5;
    const exercisePauseCount = exerciseProgramItems.length;
    return netExerciseTime + exercisePauseCount * EXERCISE_PAUSE_DURATION + totalSetPauseTime;
};
export const getTotalExerciseProgramDuration = (exerciseProgramItems, withoutTimeUnit) => {
    const totalSeconds = getTotalExerciseProgramSeconds(exerciseProgramItems);
    const totalHours = Math.floor(totalSeconds / (60 * 60));
    const remainingSeconds1 = totalSeconds - totalHours * 60 * 60;
    const totalMinutes = Math.floor(remainingSeconds1 / 60);
    const remainingSeconds2 = totalSeconds - totalMinutes * 60;
    const hourTime = totalHours > 0 ? totalHours.toString().padStart(2, '0') + ':' : '';
    const minuteTime = Math.floor(totalMinutes).toString().padStart(2, '0') +
        ':' +
        Math.round(remainingSeconds2 % 60)
            .toString()
            .padStart(2, '0');
    // `withoutTimeUnit` only drops the minute unit (the caller labels it "Dauer in
    // Min."); hours keep their `h`, or `01:05:00` would read as minutes.
    const timeUnit = totalHours > 0 ? ' h' : withoutTimeUnit ? '' : ' min';
    return hourTime + minuteTime + timeUnit;
};
export const totalMissedTrainingSessions = (exercisePrograms, trainingSessions, startDate, endDate) => {
    return exercisePrograms.reduce((acc, program) => {
        const iterationEndDate = dayjs.min(dayjs(program.endDate), endDate);
        let iterationDate = dayjs.max(startDate, dayjs(program.startDate));
        let missedProgramCounter = 0;
        while (iterationDate.isBefore(iterationEndDate.startOf('day'))) {
            if (isTrainingDay(program, iterationDate) &&
                !trainingSessions.some((trainingSession) => trainingSession.exerciseProgramId === program.id &&
                    dayjs(trainingSession.started).isSame(iterationDate, 'day'))) {
                missedProgramCounter++;
            }
            iterationDate = iterationDate.add(1, 'day');
        }
        return missedProgramCounter + acc;
    }, 0);
};
export const filterExerciseProgramsByDate = (exercisePrograms, date) => {
    const weekday = date.day();
    return exercisePrograms.filter((exerciseProgram) => {
        // Both boundaries are inclusive and compared at day granularity: `startDate`
        // and `endDate` are stored at midnight, so an instant-level comparison would
        // drop the programme's own last day for every caller that passes a real
        // clock time (e.g. the 18:00 reminder) instead of a start-of-day date.
        const isInTimeframe = !dayjs(exerciseProgram.startDate).isAfter(date, 'day') &&
            (exerciseProgram.endDate === null || !dayjs(exerciseProgram.endDate).isBefore(date, 'day'));
        if (!isInTimeframe) {
            return false;
        }
        const isOnWeekday = (exerciseProgram.trainingIsOnSunday && weekday === 0) ||
            (exerciseProgram.trainingIsOnMonday && weekday === 1) ||
            (exerciseProgram.trainingIsOnTuesday && weekday === 2) ||
            (exerciseProgram.trainingIsOnWednesday && weekday === 3) ||
            (exerciseProgram.trainingIsOnThursday && weekday === 4) ||
            (exerciseProgram.trainingIsOnFriday && weekday === 5) ||
            (exerciseProgram.trainingIsOnSaturday && weekday === 6);
        return isInTimeframe && isOnWeekday;
    });
};
export const isTrainingDay = (program, date) => (date.isSame(dayjs(program.startDate).startOf('day')) ||
    date.isAfter(dayjs(program.startDate).startOf('day'))) &&
    (!program.endDate || date.isBefore(dayjs(program.endDate).endOf('day'))) &&
    ((program.trainingIsOnSunday && date.day() === 0) ||
        (program.trainingIsOnMonday && date.day() === 1) ||
        (program.trainingIsOnTuesday && date.day() === 2) ||
        (program.trainingIsOnWednesday && date.day() === 3) ||
        (program.trainingIsOnThursday && date.day() === 4) ||
        (program.trainingIsOnFriday && date.day() === 5) ||
        (program.trainingIsOnSaturday && date.day() === 6));
/** True when not a single training weekday is toggled on. */
export const hasNoTrainingDaySelected = (program) => !program.trainingIsOnMonday &&
    !program.trainingIsOnTuesday &&
    !program.trainingIsOnWednesday &&
    !program.trainingIsOnThursday &&
    !program.trainingIsOnFriday &&
    !program.trainingIsOnSaturday &&
    !program.trainingIsOnSunday;
/**
 * Single source of truth for the invariants a frequency must satisfy before it
 * can be saved or sent: at least one training day, a start date, an end date
 * that is not in the past, and an end date that is not before the start date.
 * Shared by the basket "send program" flow and the patient-file frequency editor
 * so both enforce the same rules.
 */
export const validateExerciseProgramFrequency = (program) => {
    const noTrainingDaySelected = hasNoTrainingDaySelected(program);
    const noStartDate = !program.startDate;
    const endDateInPast = !!program.endDate && dayjs(program.endDate).isBefore(dayjs(), 'day');
    const endDateBeforeStartDate = !!program.startDate &&
        !!program.endDate &&
        dayjs(program.endDate).isBefore(dayjs(program.startDate), 'day');
    return {
        noTrainingDaySelected,
        noStartDate,
        endDateInPast,
        endDateBeforeStartDate,
        isValid: !noTrainingDaySelected && !noStartDate && !endDateInPast && !endDateBeforeStartDate,
    };
};
export const exerciseProgramExecutionButtonText = (programForCalendar) => {
    if (!programForCalendar.started && !programForCalendar.completedOrSkipped) {
        return 'Starten';
    }
    if (programForCalendar.started && !programForCalendar.completedOrSkipped) {
        return 'Fortsetzen';
    }
    if (programForCalendar.completedOrSkipped) {
        return 'Wiederholen';
    }
    if (programForCalendar.started && !programForCalendar.completedOrSkipped) {
        return 'Fortsetzen';
    }
    if (programForCalendar.completedOrSkipped) {
        return 'Wiederholen';
    }
    return 'Starten';
};
//# sourceMappingURL=exerciseProgramFunctions.js.map